import { HttpClient } from '@angular/common/http';
import { computed, Inject, Injectable, OnDestroy, signal } from '@angular/core';
import { ApiResponse, LoadingState, NotificationType } from '@app/shared/interfaces';
import { EventSourceService } from '@app/shared/services/event-source/event-source.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { Observable, of, Subject, SubscriptionLike } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { GeneratedReply, StreamEvent, StreamEventDataType } from './AI.models';

@Injectable({
  providedIn: 'root',
})
export class AIService implements OnDestroy {
  constructor(
    @Inject('apiUrl') private apiUrl: string,
    private http: HttpClient,
    private eventSourceService: EventSourceService,
    private toast: ToastNotificationsService
  ) {}

  aiSuggestionStatus = signal<LoadingState | null>(null);
  aiSuggestionProgressMessage = signal<string | null>(null);

  wasAiSuggested = computed(() => this.aiSuggestionStatus() === LoadingState.Success);
  isAiSuggestionLoading = computed(() => this.aiSuggestionStatus() === LoadingState.Pending);

  private eventSourceSubscription: SubscriptionLike;
  private stateSubscription: SubscriptionLike;

  // This is where AI messages get generated in the Inbox.
  generateReply(
    threadUuid: string,
    hostUid: string,
    generatedReplyUuid: string | null
  ): Observable<ApiResponse<GeneratedReply>> {
    return this.http.post<ApiResponse<GeneratedReply>>(`${this.apiUrl}/threads/${threadUuid}/generate-reply`, {
      host_id: hostUid,
      generated_reply_id: generatedReplyUuid,
    });
  }

  authenticateForStream(threadUuid: string, hostUid: string, generatedReplyUuid: string | null): Observable<string> {
    return this.http
      .post<ApiResponse<{ url: string }>>(`${this.apiUrl}/threads/${threadUuid}/authenticate-stream`, {
        host_id: hostUid,
        generated_reply_id: generatedReplyUuid,
      })
      .pipe(map((res) => res.data.url));
  }

  streamGenerateReply(threadUuid: string, hostUid: string, generatedReplyUuid: string | null): Observable<string> {
    const options = {};
    const events = ['update'];
    const textSubject = new Subject<string>();
    this.aiSuggestionStatus.set(LoadingState.Pending);

    this.authenticateForStream(threadUuid, hostUid, generatedReplyUuid)
      .pipe(
        switchMap((url) => {
          let accumulatedText = '';
          let hasFailed = false;
          return new Observable<string>((observer) => {
            this.eventSourceSubscription = this.eventSourceService
              .connectToServerSentEvents(url, options, events)
              .subscribe({
                next: (event) => {
                  if (event.data === '</stream>') {
                    textSubject.complete();
                    observer.complete();
                    return;
                  }

                  const data: StreamEvent = JSON.parse(event.data);

                  // If all fallbacks fail
                  if (data.type === StreamEventDataType.FAIL) {
                    console.log('Failed to generate reply');
                    hasFailed = true;
                    this.toast.open(
                      'An error occurred processing this action - please try again. If this keeps occurring, please contact support',
                      'Dismiss',
                      NotificationType.Error
                    );
                    this.aiSuggestionStatus.set(null);
                    textSubject.next('');
                    observer.error('Failed to generate reply');
                    return;
                  }

                  if (data.type === StreamEventDataType.ERROR) {
                    this.aiSuggestionProgressMessage.set('An error occurred, retrying...');
                    console.log('An error happened, retrying in the background');
                    accumulatedText = '';
                    textSubject.next('');
                    return;
                  }

                  this.aiSuggestionProgressMessage.set(null);
                  const partialReply = data.content;
                  accumulatedText += partialReply;

                  textSubject.next(accumulatedText);
                  observer.next(accumulatedText);
                },
                error: (error) => {
                  hasFailed = true;
                  console.log('Failed', error);
                  this.aiSuggestionStatus.set(null);
                  this.toast.open(
                    'An error occurred processing this action - please try again. If this keeps occurring, please contact support',
                    'Dismiss',
                    NotificationType.Error
                  );
                  textSubject.next('');
                  observer.error(error);
                },
              });

            // Cleanup when the observer unsubscribes
            return () => {
              this.aiSuggestionProgressMessage.set(null);
              this.aiSuggestionStatus.set(hasFailed ? null : LoadingState.Success);
              this.closeStream();
            };
          });
        })
      )
      .subscribe();

    return textSubject.asObservable();
  }

  closeStream(): void {
    if (this.eventSourceSubscription) {
      this.eventSourceSubscription.unsubscribe();
      this.eventSourceSubscription = null;
      this.eventSourceService.close();
    }
  }

  // This is where AI messages get improved in the Inbox.
  generateImprovement(threadUuid: string, message: string): Observable<ApiResponse<GeneratedReply>> {
    return this.http.post<ApiResponse<GeneratedReply>>(`${this.apiUrl}/threads/${threadUuid}/generate-improvement`, {
      message,
    });
  }

  /**
   * Cancel an AI message generation in progress
   */
  cancelAIPresenceMessage(uuid: string): Observable<boolean> {
    const url = `${this.apiUrl}/gx/ai/message/${uuid}`;
    return this.http.delete(url).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  ngOnDestroy(): void {
    this.closeStream();
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
    }
  }
}
